import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader, Info } from 'lucide-react';
import { paymentsApi } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';

const toInputDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const RANGES = [
  { id: 'month', label: 'This month' },
  { id: '30', label: 'Last 30 days' },
  { id: 'year', label: 'This year' },
  { id: 'all', label: 'All time' },
];

const rangeDates = (id) => {
  const now = new Date();
  if (id === 'month') return { from: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: '' };
  if (id === '30') {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return { from: toInputDate(d), to: '' };
  }
  if (id === 'year') return { from: `${now.getFullYear()}-01-01`, to: '' };
  return { from: '', to: '' };
};

/**
 * Running list of every payment recorded for the business.
 * Tapping a row opens that invoice's drawer.
 */
const PaymentsTab = ({ onOpenInvoice }) => {
  const [range, setRange] = useState('year');
  const [from, setFrom] = useState(() => rangeDates('year').from);
  const [to, setTo] = useState('');
  const [method, setMethod] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (method) params.method = method;
      const res = await paymentsApi.getAll(params);
      setData(res.data);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Could not load payments.');
    } finally {
      setLoading(false);
    }
  }, [from, to, method]);

  useEffect(() => {
    load();
  }, [load]);

  const pickRange = (id) => {
    const d = rangeDates(id);
    setRange(id);
    setFrom(d.from);
    setTo(d.to);
  };

  const openInvoice = (p) => {
    onOpenInvoice?.({
      id: p.invoiceId,
      invoiceNumber: p.invoiceNumber,
      customerName: p.customerName,
      status: p.invoiceStatus,
      totalAmount: p.invoiceTotal,
    });
  };

  const payments = data?.payments || [];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => pickRange(r.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                range === r.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <label className="label text-xs">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setRange('custom'); }}
              className="input py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="label text-xs">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => { setTo(e.target.value); setRange('custom'); }}
              className="input py-1.5 text-sm"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="label text-xs">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="input py-1.5 text-sm"
            >
              <option value="">All methods</option>
              {(data?.methods || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1 flex items-end">
            <button
              onClick={load}
              className="w-full px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center gap-2"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Total for the filtered rows */}
      {data && (
        <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #052e16, #14532d)' }}>
          <div>
            <p className="text-green-300 text-xs font-medium">Collected (filtered)</p>
            <p className="text-2xl font-bold text-green-100">{formatCurrency(data.total)}</p>
            {data.givenAway > 0 && (
              <>
                <p className="text-xs text-green-300 mt-1">
                  Given away (value): {formatCurrency(data.givenAway)}
                </p>
                <p className="text-xs text-green-200 font-medium">
                  Total value of work: {formatCurrency(data.total + data.givenAway)}
                </p>
              </>
            )}
          </div>
          <p className="text-sm text-green-300">
            {data.count} payment{data.count !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* List */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      ) : loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-gray-400" size={28} />
        </div>
      ) : payments.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No payments found for these filters.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
          {payments.map((p) => (
            <button
              key={p.id}
              onClick={() => openInvoice(p)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{p.customerName || 'Unknown customer'}</p>
                  <p className="text-xs text-gray-500">
                    {formatDate(p.paymentDate)} • {p.paymentMethod} • {p.invoiceNumber || `INV-${p.invoiceId}`}
                  </p>
                  {p.transactionId && (
                    <p className="text-xs text-gray-400 truncate">Ref: {p.transactionId}</p>
                  )}
                </div>
                {p.paymentMethod === 'Personal/Free' && p.jobValue > 0 ? (
                  <div className="text-right whitespace-nowrap">
                    <p className="font-semibold text-slate-600">{formatCurrency(p.jobValue)}</p>
                    <p className="text-xs text-slate-400">value given</p>
                  </div>
                ) : (
                  <p className="font-semibold text-green-700 whitespace-nowrap">{formatCurrency(p.amount)}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {data?.truncated && (
        <p className="text-xs text-amber-700">
          Showing the newest 500 of {data.count} payments. The total above covers all of them. Narrow the dates to see older ones.
        </p>
      )}

      <p className="text-xs text-gray-400 flex items-center gap-1.5">
        <Info size={12} />
        Refunds made in Stripe aren't tracked here yet, so a refunded card payment still appears.
      </p>
    </div>
  );
};

export default PaymentsTab;